// Account panel for the compact top bar (below the 1024px desktop floor), opened from the avatar. It holds
// everything the condensed bar drops, so nothing is lost on a tablet or phone: the season switch,
// Settings, Tickets, Administration, the co-op switch, who else is on this page (nudge / message / start co-op)
// and Log out. A bottom sheet at every width; Modal brings focus, Escape, the scrim and the close x.
import { useLocation } from 'react-router-dom';
import { roleLabel, TICKETS_LINK } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { ADMIN_PERMS } from '../app/nav.js';
import { usePresence } from '../features/hooks.js';
import { useCollab } from '../collab/CollabContext.js';
import { Avatar } from './Avatar.js';
import { InlineError, Modal } from './ui.js';
import { IconChat, IconChevronRight, IconCursor, IconNudge } from './icons.js';

export function AccountSheet({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  /** leave the sheet for another page (AppShell's overlay-aware navigate, so Back stays honest). */
  onNavigate: (path: string, close: () => void) => void;
}) {
  const { me, can, logout } = useAuth();
  const { activeSeason, currentSeason, seasons, setActiveSeason, isCurrent } = useSeason();
  const { pathname } = useLocation();
  const { data } = usePresence(pathname); // same query as the top-bar dock, so no extra request
  const collab = useCollab();
  if (!me) return null;

  const viewers = (data?.viewers ?? []).filter((v) => v.uid);
  const isAdmin = ADMIN_PERMS.some((p) => can(p));
  const peers = collab.coopPeers.length;
  // a person action closes the sheet, like the dock's avatar menu, so the result (chat, banner) shows
  const act = (fn: () => void) => () => { fn(); onClose(); };

  return (
    <Modal sheet title="Account" onClose={onClose}>
      <div className="acct">
        <div className="acct-id">
          <Avatar name={me.user.displayName} avatar={me.user.avatar} size={44} />
          <div className="acct-id-main">
            <div className="acct-id-name">{me.user.displayName}</div>
            <div className="acct-id-meta">{roleLabel(me.user.role)}</div>
            <div className="acct-id-meta acct-id-email">{me.user.email}</div>
          </div>
        </div>

        {seasons.length > 1 ? (
          <div className="acct-season">
            <label className="field-label" htmlFor="acct-season">Season</label>
            <select id="acct-season" className={`field${isCurrent ? '' : ' past'}`} value={activeSeason} onChange={(e) => setActiveSeason(e.target.value)}>
              {seasons.map((id) => (
                <option key={id} value={id}>
                  Season {id}{id === currentSeason ? ' (current)' : ''}
                </option>
              ))}
            </select>
            {!isCurrent && <div className="acct-hint">Viewing {activeSeason}. The current season is {currentSeason}.</div>}
          </div>
        ) : (
          // one season: nothing to switch, but phones drop the bar's "Season …" label, so it shows here
          activeSeason && <div className="acct-hint">Season {activeSeason}</div>
        )}

        <div className="acct-list">
          <button className="acct-row" aria-current={pathname === '/settings' ? 'page' : undefined} onClick={() => onNavigate('/settings', onClose)}>
            <span className="acct-row-main">Settings</span>
            <IconChevronRight size={16} className="acct-chev" />
          </button>
          <button className="acct-row" onClick={() => onNavigate(TICKETS_LINK, onClose)}>
            <span className="acct-row-main">
              <span>Tickets</span>
              <span className="acct-hint">Send a ticket to the administrators, and see their answers</span>
            </span>
            <IconChevronRight size={16} className="acct-chev" />
          </button>
          {isAdmin && (
            <button className="acct-row" aria-current={pathname.startsWith('/admin') ? 'page' : undefined} onClick={() => onNavigate('/admin', onClose)}>
              <span className="acct-row-main">Administration</span>
              <IconChevronRight size={16} className="acct-chev" />
            </button>
          )}
          <label className="acct-row acct-switch">
            <span className="acct-row-main">
              <span>Co-op on this page</span>
              <span className="acct-hint">
                {collab.coopOn
                  ? peers > 0 ? `On · ${peers} ${peers === 1 ? 'other' : 'others'} sharing cursors` : 'On · waiting for others'
                  : 'Share cursors here; your view follows the group.'}
              </span>
            </span>
            <input type="checkbox" role="switch" checked={collab.coopOn} onChange={collab.toggleCoop} />
          </label>
        </div>

        {viewers.length > 0 && (
          <section className="acct-here" aria-label="Also on this page">
            <div className="acct-sec-hd">Also here ({viewers.length})</div>
            {collab.nudgeError && (
              <InlineError error={collab.nudgeError.error} onDismiss={collab.clearNudgeError} />
            )}
            {viewers.map((v) => (
              <div key={v.uid} className="acct-person">
                <div className="acct-person-id">
                  <Avatar name={v.name} avatar={v.avatar} size={32} />
                  <div className="acct-id-main">
                    <div className="acct-person-name">{v.name}</div>
                    {v.department && <div className="acct-id-meta">{v.department}</div>}
                  </div>
                </div>
                <div className="acct-person-acts">
                  <button className="btn" onClick={act(() => collab.nudge(v.uid!, 'poke'))} aria-label={`Nudge ${v.name}`}>
                    <IconNudge size={15} /> Nudge
                  </button>
                  <button className="btn" onClick={act(() => collab.openDmWith(v.uid!))} aria-label={`Message ${v.name}`}>
                    <IconChat size={15} /> Message
                  </button>
                  <button className="btn" onClick={act(() => collab.startCoopWith(v.uid!))} aria-label={`Start co-op with ${v.name}`}>
                    <IconCursor size={15} /> Start co-op
                  </button>
                </div>
              </div>
            ))}
          </section>
        )}

        <div className="acct-list">
          <button className="acct-row acct-row--danger" onClick={() => { onClose(); void logout(); }}>
            <span className="acct-row-main">Log out</span>
          </button>
        </div>
      </div>
    </Modal>
  );
}
